import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, History, X } from 'lucide-react'
import { Button } from '@/components/ui'
import { ToneBadge } from '@/components/ui/Tone'
import { formatPartnerDisplay } from '@/lib/utils'
import type { PartnerCoverageRow } from '@/types'
import {
  PARTNER_SERVICE_CATEGORIES,
  partnerServiceCategoryMeta,
  setPartnerServiceCategory,
  getPartnerServiceCategoryHistory,
  cancelPartnerServiceCategoryChange,
  type PartnerServiceCategory,
  type PartnerServiceCategoryRow,
  type PartnerServiceCategoryHistoryRow,
} from '@/services/partnerServiceCategoryService'

const ru = (iso: string | null | undefined) => (iso ? iso.split('-').reverse().join('.') : '—')

export function PartnerServiceCategoryBadge({
  row,
  onClick,
}: {
  row: PartnerServiceCategoryRow | undefined
  onClick?: () => void
}) {
  const meta = partnerServiceCategoryMeta(row?.category)
  const next = partnerServiceCategoryMeta(row?.next_category)
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1" title="История категории">
      <ToneBadge tone={meta?.tone ?? 'neutral'} size="md">
        {meta ? `${meta.label} · с ${ru(row?.valid_from)}` : 'без категории'}
      </ToneBadge>
      {next && row?.next_from && (
        <ToneBadge tone="neutral" size="md">→ {next.value} с {ru(row.next_from)}</ToneBadge>
      )}
    </button>
  )
}

interface Transition {
  row: PartnerCoverageRow
  from: PartnerServiceCategory | null
  kind: 'created' | 'changed' | 'replaced' | 'unchanged' | 'blocked'
}

// Форма массового перевода: категория, дата перехода, причина. Подтверждение показывает
// переходы по направлениям и сколько студий начнут или перестанут получать отсмотры.
export function PartnerServiceCategoryForm({
  selectedRows,
  categories,
  today,
  onDone,
  onCancel,
}: {
  selectedRows: PartnerCoverageRow[]
  categories: Map<number, PartnerServiceCategoryRow>
  today: string
  onDone: () => void
  onCancel: () => void
}) {
  const [category, setCategory] = useState<PartnerServiceCategory | ''>('')
  const [effectiveFrom, setEffectiveFrom] = useState(today)
  const [reason, setReason] = useState('')
  const [reviewOn, setReviewOn] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const transitions = useMemo<Transition[]>(() => {
    if (!category) return []
    return selectedRows.map(row => {
      const cur = categories.get(row.partner_number)
      // Сервер отказывает, если после даты перехода у франчайзи уже есть смена: такие не шлём.
      if ([cur?.valid_from, cur?.next_from].some(d => d && d > effectiveFrom)) {
        return { row, from: cur?.category ?? null, kind: 'blocked' }
      }
      // Категория на дату перехода — запланированная (если уже наступит) или текущая.
      const from = cur?.next_from ? cur.next_category : cur?.category ?? null
      const fromStart = cur?.next_from ?? cur?.valid_from ?? null
      if (!from) return { row, from: null, kind: 'created' }
      if (from === category) return { row, from, kind: 'unchanged' }
      return { row, from, kind: fromStart === effectiveFrom ? 'replaced' : 'changed' }
    })
  }, [selectedRows, categories, category, effectiveFrom])

  const effective = transitions.filter(t => t.kind !== 'unchanged' && t.kind !== 'blocked')
  const blocked = transitions.filter(t => t.kind === 'blocked')
  const studiosLeaving = effective
    .filter(t => category === 4 && t.from !== 4)
    .reduce((s, t) => s + (t.row.studio_count ?? 0), 0)
  const studiosReturning = effective
    .filter(t => category !== 4 && t.from === 4)
    .reduce((s, t) => s + (t.row.studio_count ?? 0), 0)

  const groups = useMemo(() => {
    const map = new Map<string, Transition[]>()
    for (const t of effective) {
      const key = t.kind === 'replaced'
        ? `Исправление с той же даты: ${t.from ?? '—'} → ${category}`
        : `${t.from ? `Категория ${t.from}` : 'Без категории'} → ${category}`
      map.set(key, [...(map.get(key) ?? []), t])
    }
    return [...map.entries()]
  }, [effective, category])

  const canSubmit = !!category && !!effectiveFrom && reason.trim().length > 0 && !busy

  const apply = async () => {
    if (!category) return
    setBusy(true)
    setError(null)
    try {
      await setPartnerServiceCategory({
        partnerNumbers: effective.map(t => t.row.partner_number),
        category,
        effectiveFrom,
        reason: reason.trim(),
        reviewOn: reviewOn || null,
      })
      setConfirmOpen(false)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="text-xs text-muted-foreground space-y-1">
          <span>Категория</span>
          <select
            value={category}
            onChange={e => setCategory(e.target.value ? (Number(e.target.value) as PartnerServiceCategory) : '')}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm text-foreground"
          >
            <option value="">Выберите категорию…</option>
            {PARTNER_SERVICE_CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label} — {c.services}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-muted-foreground space-y-1">
          <span>Первый день в новой категории</span>
          <input
            type="date"
            value={effectiveFrom}
            onChange={e => setEffectiveFrom(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm text-foreground"
          />
        </label>
        <label className="text-xs text-muted-foreground space-y-1 sm:col-span-2">
          <span>Причина (обязательно)</span>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Например: три месяца без оплаты роялти"
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm text-foreground"
          />
        </label>
        <label className="text-xs text-muted-foreground space-y-1">
          <span>Пересмотреть (необязательно)</span>
          <input
            type="date"
            value={reviewOn}
            onChange={e => setReviewOn(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm text-foreground"
          />
        </label>
      </div>
      <div className="text-xs text-muted-foreground">
        Прежняя категория закроется днём раньше выбранной даты.
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!canSubmit} onClick={() => { setError(null); setConfirmOpen(true) }}>
          Применить
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Отменить</Button>
      </div>
      {error && !confirmOpen && <div className="text-xs text-destructive">Ошибка: {error}</div>}

      {confirmOpen && category && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => { if (!busy) setConfirmOpen(false) }}
        >
          <div
            className="bg-card rounded-card shadow-lg w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-border">
              <div className="text-sm font-semibold">Перевод в категорию {category}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                С {ru(effectiveFrom)}. Выбрано {selectedRows.length}, изменений: {effective.length}.
                Причина: <span className="text-foreground">{reason.trim()}</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm">
              {studiosLeaving > 0 && (
                <div className="rounded-md border border-error/40 bg-error/5 p-2.5 text-xs flex gap-1.5">
                  <AlertTriangle size={14} className="shrink-0 text-error" />
                  <span>
                    У {studiosLeaving} студий перестанут выдаваться отсмотры.
                    {effectiveFrom < today && ' Уже выданные до сегодняшнего дня задания не отменяются.'}
                  </span>
                </div>
              )}
              {studiosReturning > 0 && (
                <div className="rounded-md border border-success/40 bg-success/5 p-2.5 text-xs">
                  У {studiosReturning} студий снова начнут выдаваться отсмотры.
                </div>
              )}
              {groups.map(([title, items]) => (
                <div key={title} className="rounded-md border border-border bg-muted/20 p-2.5">
                  <div className="text-xs font-semibold uppercase tracking-wide mb-1.5">{title} · {items.length}</div>
                  <div className="space-y-1">
                    {items.map(t => (
                      <div key={t.row.partner_number} className="text-sm">
                        {formatPartnerDisplay(t.row.partner_number, t.row.persons_fio, t.row.cities_text)}
                        <span className="text-xs text-muted-foreground"> · {t.row.studio_count} студий</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {blocked.length > 0 && (
                <div className="rounded-md border border-primary/40 bg-primary/5 p-2.5 text-xs">
                  <div className="font-semibold mb-1">Не будут изменены — позже уже запланирована смена · {blocked.length}</div>
                  {blocked.map(t => (
                    <div key={t.row.partner_number}>
                      {formatPartnerDisplay(t.row.partner_number, t.row.persons_fio, t.row.cities_text)}
                    </div>
                  ))}
                  <div className="text-muted-foreground mt-1">Сначала отмените запланированную смену в истории франчайзи.</div>
                </div>
              )}
              {transitions.some(t => t.kind === 'unchanged') && (
                <div className="text-xs text-muted-foreground">
                  Уже в категории {category}: {transitions.filter(t => t.kind === 'unchanged').length} — будут пропущены.
                </div>
              )}
              {error && (
                <div className="text-xs text-destructive flex items-center gap-1">
                  <AlertTriangle size={12} /> {error}
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-border flex flex-wrap items-center justify-end gap-2">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirmOpen(false)}>Назад</Button>
              <Button size="sm" variant="secondary" disabled={busy || effective.length === 0} onClick={apply}>
                {busy ? 'Применяем…' : `Перевести (${effective.length})`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// История категорий франчайзи; advanced может отменить последнюю смену.
export function PartnerServiceCategoryHistoryModal({
  partner,
  canEdit,
  onClose,
  onChanged,
}: {
  partner: PartnerCoverageRow
  canEdit: boolean
  onClose: () => void
  onChanged: () => void
}) {
  const [rows, setRows] = useState<PartnerServiceCategoryHistoryRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = () => {
    getPartnerServiceCategoryHistory(partner.partner_number)
      .then(r => { setRows(r); setError(null) })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
  }
  useEffect(load, [partner.partner_number])

  const cancelLast = async (changeId: number) => {
    setBusy(true)
    setError(null)
    try {
      await cancelPartnerServiceCategoryChange(changeId)
      load()
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => { if (!busy) onClose() }}>
      <div className="bg-card rounded-card shadow-lg w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-border flex items-start justify-between gap-2">
          <div>
            <div className="text-sm font-semibold flex items-center gap-1.5"><History size={14} /> История категории</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {formatPartnerDisplay(partner.partner_number, partner.persons_fio, partner.cities_text)}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 text-sm">
          {rows === null && !error && <div className="text-muted-foreground">Загрузка…</div>}
          {rows?.length === 0 && <div className="text-muted-foreground">Категория ещё не ставилась.</div>}
          {rows?.map((r, i) => {
            const meta = partnerServiceCategoryMeta(r.category)
            return (
              <div key={r.change_id} className="rounded-md border border-border p-2.5 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <ToneBadge tone={meta?.tone ?? 'neutral'} size="md">{meta?.label ?? r.category}</ToneBadge>
                  <span className="text-xs text-muted-foreground">
                    {ru(r.valid_from)} — {r.valid_to ? ru(r.valid_to) : 'по сей день'}
                  </span>
                  {r.review_on && <span className="text-xs text-muted-foreground">· пересмотр {ru(r.review_on)}</span>}
                </div>
                <div className="text-xs">{r.reason}</div>
                <div className="text-[11px] text-muted-foreground">
                  Поставил: {r.set_by_name ?? '—'}
                  {r.closed_by_name && <> · закрыл: {r.closed_by_name}</>}
                </div>
                {canEdit && i === 0 && (
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => cancelLast(r.change_id)}>
                    {busy ? 'Отменяем…' : rows.length > 1 ? 'Отменить эту смену' : 'Снять категорию'}
                  </Button>
                )}
              </div>
            )
          })}
          {error && <div className="text-xs text-destructive">Ошибка: {error}</div>}
        </div>
      </div>
    </div>
  )
}
